import React, { Suspense } from 'react'; // Core library
import { lazy } from 'react';
import { Navigate, useRoutes } from 'react-router-dom';
import ListingSettings from 'src/pages/dashboard/Listing/Settings';

import LoadingScreen from '../components/LoadingScreen';
import { PATH_AFTER_LOGIN } from '../config';
import DashboardLayout from '../layouts/dashboard';

const Loadable = (Component) => {
  const WrappedComponent = (props) => {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <Component {...props} />
      </Suspense>
    );
  };

  // Set the displayName for better debugging
  WrappedComponent.displayName = `Loadable(${Component.displayName || Component.name || 'Component'})`;

  return WrappedComponent;
};

// AUTHENTICATION
const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const CompanyLogin = Loadable(lazy(() => import('../pages/auth/CompanyLogin')));
const Register = Loadable(lazy(() => import('../pages/auth/Register')));
const ResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));
const CodeMail = Loadable(lazy(() => import('../pages/auth/CodeMail')));

const Dorm = Loadable(lazy(() => import('../pages/dashboard/Subscription/Dorm')));
const EditDorm = Loadable(lazy(() => import('../pages/dashboard/Subscription/EditDorm')));
const AddDorm = Loadable(lazy(() => import('../pages/dashboard/Subscription/AddDorm')));
const EmailManagement = Loadable(lazy(() => import('../pages/dashboard/EmailManagement/EmailManagement')));

// const Element = Loadable(lazy(() => import('../pages/dashboard/Company/Element')));
// const EditElement = Loadable(lazy(() => import('../pages/dashboard/Company/EditElement')));
const Comment = Loadable(lazy(() => import('../pages/dashboard/Comments/Comments')));
// const Media = Loadable(lazy(() => import('../pages/dashboard/Media/Media')));
// const AddMedia = Loadable(lazy(() => import('../pages/dashboard/Media/AddMedia')));
// const EditMedia = Loadable(lazy(() => import('../pages/dashboard/Media/EditMedia')));
const AddComment = Loadable(lazy(() => import('../pages/dashboard/Comments/AddComment')));
const EditComment = Loadable(lazy(() => import('../pages/dashboard/Comments/EditComment')));
const ReplyComment = Loadable(lazy(() => import('../pages/dashboard/Comments/replycomment')));
const CommentFilter = Loadable(lazy(() => import('../pages/dashboard/Comments/CommentFilter')));
const CompanyProfile = Loadable(lazy(() => import('../pages/dashboard/Company/CompanyProfile')));
const AddElement = Loadable(lazy(() => import('../pages/dashboard/Company/AddElement')));

const Rarity = Loadable(lazy(() => import('../pages/dashboard/TermCondition.js/Rarity')));
const EditRarity = Loadable(lazy(() => import('../pages/dashboard/TermCondition.js/EditRarity')));
const AddRarity = Loadable(lazy(() => import('../pages/dashboard/TermCondition.js/AddRarity')));
const EditAdmin = Loadable(lazy(() => import('../pages/dashboard/EditAdmin/EditAdmin')));

const Review = Loadable(lazy(() => import('../pages/dashboard/Review/Review')));
const AddReview = Loadable(lazy(() => import('../pages/dashboard/Review/AddReview')));
const EditReview = Loadable(lazy(() => import('../pages/dashboard/Review/EditReview')));
// const Comments = Loadable(lazy(() => import('../pages/dashboard/Comments/Comments')));
// const EditPage = Loadable(lazy(() => import('../pages/dashboard/Page/EditPage')));
const Spell = Loadable(lazy(() => import('../pages/dashboard/user/user')));
const UserDetail = Loadable(lazy(() => import('../pages/dashboard/user/user-detail')));
// const EmailManagement = Loadable(lazy(() => import('../pages/dashboard/EmailManagement/EmailManagement')));
const Tags = Loadable(lazy(() => import('../pages/dashboard/Tags/Tags')));
const AddTags = Loadable(lazy(() => import('../pages/dashboard/Tags/AddTags')));
const EditTags = Loadable(lazy(() => import('../pages/dashboard/Tags/EditTags')));
// const Category = Loadable(lazy(() => import('../pages/dashboard/Category/Category')));
// const AddCategory = Loadable(lazy(() => import('../pages/dashboard/Category/AddCategory')));
// const EditCategory = Loadable(lazy(() => import('../pages/dashboard/Category/EditCategory')));

const ViewUser = Loadable(lazy(() => import('../pages/dashboard/user/view-user')));

const EditUser = Loadable(lazy(() => import('../pages/dashboard/user/edit-user')));
const EditUserPrev = Loadable(lazy(() => import('../pages/dashboard/user/edit-user/indexPrev')));
const CancellationPolicy = Loadable(lazy(() => import('../pages/dashboard/Cancellation/CancellationPolicy')));
const AddCancellationPolicy = Loadable(lazy(() => import('../pages/dashboard/Cancellation/AddCancellationPolicy')));
const EditCancellationPolicy = Loadable(lazy(() => import('../pages/dashboard/Cancellation/AddCancellationPolicy')));
const GeneralAnalytics = Loadable(lazy(() => import('../pages/dashboard/GeneralAnalytics')));
const Host = Loadable(lazy(() => import('../pages/dashboard/Host/Host')));
const AddHost = Loadable(lazy(() => import('../pages/dashboard/Host/AddHost')));
const EditHost = Loadable(lazy(() => import('../pages/dashboard/Host/EditHost')));

const Feature = Loadable(lazy(() => import('../pages/dashboard/Feature')));
const AddFeature = Loadable(lazy(() => import('../pages/dashboard/Feature/AddFeature')));
const EditFeature = Loadable(lazy(() => import('../pages/dashboard/Feature/EditFeature')));
const Listing = Loadable(lazy(() => import('../pages/dashboard/Listing/Listing')));
const AddListing = Loadable(lazy(() => import('../pages/dashboard/Listing/AddListingForm')));
const AddListingPrev = Loadable(lazy(() => import('../pages/dashboard/Listing/AddListing')));
const EditListing = Loadable(lazy(() => import('../pages/dashboard/Listing/EditListing')));
const Duplicate = Loadable(lazy(() => import('../pages/dashboard/Listing/Duplicate')));

const ViewLisitng = Loadable(lazy(() => import('../pages/dashboard/Listing/ViewLisitng')));
const Booking = Loadable(lazy(() => import('../pages/dashboard/Booking/Booking')));
const ViewBooking = Loadable(lazy(() => import('../pages/dashboard/Booking/ViewBooking')));
const ExtraCharges = Loadable(lazy(() => import('../pages/dashboard/Booking/ExtraCharge')));
const ConfirmBooking = Loadable(lazy(() => import('../pages/dashboard/Booking/ConfirmBooking')));
const Restaurants = Loadable(lazy(() => import('../pages/restaurants')));
const AddRestaurants = Loadable(lazy(() => import('../pages/restaurants/add-restaurants')));

const Map = Loadable(lazy(() => import('../pages/dashboard/Listing/MapLocation')));

const Message = Loadable(lazy(() => import('../pages/dashboard/Message/Message')));
const Chat = Loadable(lazy(() => import('../pages/dashboard/Chat/Chat')));
const Payouts = Loadable(lazy(() => import('../pages/dashboard/Payouts/Payouts')));
const PayoutsSearch = Loadable(lazy(() => import('../pages/dashboard/Payouts/PayoutsSearch')));
const PayoutsDetails = Loadable(lazy(() => import('../pages/dashboard/Payouts/PayoutsDetails')));
const Invoices = Loadable(lazy(() => import('../pages/dashboard/Invoices/Invoices')));
const InvoicesDetails = Loadable(lazy(() => import('../pages/dashboard/Invoices/InvoicesDetails')));

export default function Router() {
  return useRoutes([
    { path: '/', element: <Navigate to="/login/admin" replace /> },
    { path: '/login/admin', element: <Login /> },
    { path: '/codemail', element: <CodeMail /> },
    { path: '/login/company', element: <CompanyLogin /> },
    { path: '/forget', element: <Register /> },
    { path: '/reset-passsword', element: <ResetPassword /> },
    {
      path: 'dashboard',
      element: <DashboardLayout />,
      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        { path: 'restaurants', element: <Restaurants /> },
        { path: 'restaurants/add-restaurant', element: <AddRestaurants /> },
        { path: 'home', element: <GeneralAnalytics /> },
        { path: 'dorm', element: <Dorm /> },
        { path: 'editdorm/:id', element: <EditDorm /> },
        { path: 'adddorm', element: <AddDorm /> },
        // { path: 'element', element: <Element /> },
        // { path: 'editelement/:id', element: <EditElement /> },
        { path: 'comment/:id', element: <Comment /> },
        { path: 'companyProfile', element: <CompanyProfile /> },
        { path: 'addelement', element: <AddElement /> },
        { path: 'rarity', element: <Rarity /> },
        { path: 'editrarity/:id', element: <EditRarity /> },
        { path: 'addrarity', element: <AddRarity /> },
        { path: 'user', element: <Spell /> },
        { path: 'user/viewuser/:id', element: <ViewUser /> },
        { path: 'user/userdetail', element: <UserDetail /> },
        { path: 'user/edit-user/:id', element: <EditUser /> },
        { path: 'user/edit-user-prev/:id', element: <EditUserPrev /> },
        { path: 'editadmin', element: <EditAdmin /> },
        { path: 'review', element: <Review /> },
        { path: 'addreview', element: <AddReview /> },
        { path: 'editreview/:id', element: <EditReview /> },
        { path: 'addcomment', element: <AddComment /> },
        { path: 'editcomment/:id', element: <EditComment /> },
        { path: 'replycomment/:id', element: <ReplyComment /> },
        { path: 'commentfilter', element: <CommentFilter /> },
        { path: 'host', element: <Host /> },
        { path: 'addhost', element: <AddHost /> },
        { path: 'edithost/:id', element: <EditHost /> },

        { path: 'listing', element: <Listing /> },
        { path: 'listing/addlisting', element: <AddListing /> },
        { path: 'listing/settings/:id', element: <ListingSettings /> },

        { path: 'feature', element: <Feature /> },
        { path: 'addfeature', element: <AddFeature /> },
        { path: 'editfeature/:id', element: <EditFeature /> },

        { path: 'addlistingprev', element: <AddListingPrev /> },
        { path: 'listing/editlisting/:id', element: <EditListing /> },
        { path: 'duplicatelisting/:id', element: <Duplicate /> },
        { path: 'viewlisting/:id', element: <ViewLisitng /> },
        { path: 'booking', element: <Booking /> },
        { path: 'booking/viewbooking/:id', element: <ViewBooking /> },
        { path: 'extracharges/:id', element: <ExtraCharges /> },
        { path: 'confirmbooking/:id', element: <ConfirmBooking /> },
        { path: 'maplocation', element: <Map /> },
        { path: 'message', element: <Message /> },
        { path: 'chat', element: <Chat /> },
        { path: 'payouts', element: <Payouts /> },
        { path: 'payoutssearch', element: <PayoutsSearch /> },
        { path: 'payoutsdetails', element: <PayoutsDetails /> },
        { path: 'invoices', element: <Invoices /> },
        { path: 'invoicesdetails', element: <InvoicesDetails /> },
        { path: 'cancellation-policy', element: <CancellationPolicy /> },
        { path: 'add-cancellation-policy', element: <AddCancellationPolicy /> },
        { path: 'edit-cancellation-policy/:id', element: <EditCancellationPolicy /> },
        { path: 'tags', element: <Tags /> },
        { path: 'addtag', element: <AddTags /> },
        { path: 'edittag/:id', element: <EditTags /> },

        { path: 'emailmanagement', element: <EmailManagement /> },
      ],
    },
  ]);
}
